import React from 'react';

import {createVars} from '../createVars';

import {useTheme} from '../../LegacyThemeContext';
import designer from '../../designer';

import type {Themes, CreateLegacyVars} from './createLegacyVars.types';

const themes: Themes = {
  designer,
};

/**
 * A temporary interop utility to access legacy theme-aware values in emotionStyled.
 *
 * It generates a stable css token list and creates a css vars object for injecting them inline.
 *
 * The mapping object keys do not need to be associated with any specific CSS Properties.
 *
 * They should be named in a clear and concise way to increase understanding and readability.
 *
 * <em>Unitless values <strong>must</strong> use specific reserved keys to remain unitless. [For a list of reserved keywords see unitlessKeys.js](../unitlessKeys.js).
 *
 * # Example
 *
 * ```javascript
 * const {localTokens, useLegacyTheme} = createLegacyVars(theme => ({
 *  fontSize: theme.typography.heading.fontSize,
 *  primary: {
 *    backgroundColor: theme.Button.primary.background || theme.colors.accentBright
 *  },
 *  lineHeight: theme.lineHeights.text,
 *  arbitrary: {
 *    token: {
 *      name: theme.Button.warning.background
 *    }
 *  }
 * }));
 *
 * const SomeComponent = emotionStyled('div')({
 *  fontSize: localTokens.fontSize,
 *  lineHeight: localTokens.lineHeight,
 * }, {
 *  variants: {
 *    variant: {
 *      primary: {
 *        backgroundColor: localTokens.primary.backgroundColor
 *      },
 *      warning: {
 *        backgroundColor: localTokens.arbitrary.token.name
 *      }
 *    }
 *  }
 * });
 *
 * const Usage = () => {
 *  const cssVars = useLegacyTheme()
 *
 *  return (
 *    <SomeComponent style={cssVars} variant='primary' />
 *  )
 * };
 * ```
 */
export const createLegacyVars: CreateLegacyVars = (
  binding,
  {baseTheme = 'designer'} = {}
) => {
  // Maps our binding
  const mappedBindings = binding(themes[baseTheme]);

  // Convert arbitrarily deeply nested bindings into tokens, using their path as the var name.
  // We convert unitless values into pixels where appropriate.
  const [localTokens, setLocalTokens] = createVars(mappedBindings, {
    shouldConvertToPixels: true,
  });

  const useLegacyTheme = () => {
    const theme = useTheme();
    const themeRef = React.useRef({
      cssVars: setLocalTokens(),
      theme: baseTheme,
    });

    // We only want to rerun and map through our binding function if we're in a new theme context
    // eslint-disable-next-line webflow/no-theme-name
    if (theme.name === themeRef.current.theme) {
      return themeRef.current.cssVars;
    }

    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'BaseTheme'.
    // eslint-disable-next-line webflow/no-theme-name
    themeRef.current.theme = theme.name;
    const remappedBindings = binding(theme);
    themeRef.current.cssVars = setLocalTokens(remappedBindings);
    return themeRef.current.cssVars;
  };

  return {
    localTokens,
    useLegacyTheme,
  };
};
